import { 
  VideoPromptSession, 
  GenerateVideoIterationRequest, 
  VideoIterationResponse,
  GenerateVideoRequest,
  VideoGenerationResponse,
  VideoPromptIteration
} from '../models/videoPrompt';
import { apiClient } from './api';

const STORAGE_KEY = 'video-prompt-sessions';

export class VideoPromptService {
  private static BASE_URL = '/api/videoprompt';

  // Start a new session via API
  static async startSession(initialPrompt: string, sessionType: string = 'Standard'): Promise<any> {
    const response = await apiClient.post(`${this.BASE_URL}/session/start`, {
      InitialPrompt: initialPrompt,
      SessionType: sessionType
    });
    return response.data;
  }

  // Session Management
  static loadSessions(): VideoPromptSession[] {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return [];
    
    const sessions = JSON.parse(stored);
    return sessions.map((session: any) => ({
      ...session,
      createdAt: new Date(session.createdAt),
      lastUpdatedAt: new Date(session.lastUpdatedAt),
      iterations: session.iterations.map((iter: any) => ({
        ...iter,
        timestamp: new Date(iter.timestamp)
      }))
    }));
  }

  static saveSessions(sessions: VideoPromptSession[]): void {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(sessions));
  }

  static saveSession(session: VideoPromptSession): void {
    const sessions = this.loadSessions();
    const existingIndex = sessions.findIndex(s => s.id === session.id);
    
    if (existingIndex >= 0) {
      sessions[existingIndex] = session;
    } else {
      sessions.push(session);
    }
    
    this.saveSessions(sessions);
  }

  static createSession(title: string, initialPrompt: string): VideoPromptSession {
    const sessions = this.loadSessions();
    const newSession: VideoPromptSession = {
      id: Date.now().toString(),
      title,
      initialPrompt,
      currentPrompt: initialPrompt,
      sessionType: 'Standard',
      createdAt: new Date(),
      lastUpdatedAt: new Date(),
      iterations: []
    };
    
    sessions.push(newSession);
    this.saveSessions(sessions);
    return newSession;
  }

  static updateSession(session: VideoPromptSession): void {
    const sessions = this.loadSessions();
    const index = sessions.findIndex(s => s.id === session.id);
    if (index !== -1) {
      sessions[index] = {
        ...session,
        lastUpdatedAt: new Date()
      };
      this.saveSessions(sessions);
    }
  }

  static deleteSession(sessionId: string): void {
    const sessions = this.loadSessions();
    const filtered = sessions.filter(s => s.id !== sessionId);
    this.saveSessions(filtered);
  }

  static getSession(sessionId: string): VideoPromptSession | undefined {
    const sessions = this.loadSessions();
    return sessions.find(s => s.id === sessionId);
  }

  // API Interactions
  static async generateIteration(sessionId: string, request: GenerateVideoIterationRequest): Promise<VideoIterationResponse> {
    try {
      const response = await apiClient.post(`${this.BASE_URL}/session/${sessionId}/iterate`, {
        AgentType: request.agentType,
        MaxResponseTokens: request.maxResponseTokens || 2048,
        Temperature: request.temperature || 0.7,
        AdditionalInstructions: request.additionalInstructions,
        SessionParams: request.sessionParams,
        VideoParams: request.videoParams
      });
      
      console.log('Video iteration API response:', response.data);
      
      // Map the response fields to match our expected interface
      const mappedResponse: VideoIterationResponse = {
        sessionId: response.data.sessionId,
        iterationNumber: response.data.iterationNumber,
        originalPrompt: response.data.previousPrompt || response.data.originalPrompt,
        processedPrompt: response.data.improvedPrompt || response.data.processedPrompt,
        videoGenerationUrl: response.data.videoGenerationUrl,
        thumbnailUrl: response.data.thumbnailUrl,
        metadata: response.data.metadata,
        error: response.data.error || response.data.errors
      };
      
      return mappedResponse;
    } catch (error) {
      console.error('Failed to generate iteration:', error);
      throw error;
    }
  }

  static async generateVideo(request: GenerateVideoRequest): Promise<VideoGenerationResponse> {
    try {
      const response = await apiClient.post(`${this.BASE_URL}/generate-video`, {
        Prompt: request.prompt,
        Duration: request.duration || 5,
        AspectRatio: request.aspectRatio || '16:9',
        Style: request.style || 'default'
      });
      return response.data;
    } catch (error) {
      console.error('Failed to generate video:', error);
      throw error;
    }
  }

  static async checkVideoStatus(generationId: string): Promise<VideoGenerationResponse> {
    try {
      const response = await apiClient.get(`${this.BASE_URL}/video-status/${generationId}`);
      return response.data;
    } catch (error) {
      console.error('Failed to check video status:', error);
      throw error;
    }
  }

  static async bulkIterate(sessionId: string, iterationCount: number = 3): Promise<any> {
    try {
      const response = await apiClient.post(`${this.BASE_URL}/session/${sessionId}/bulk-iterate`, {
        IterationCount: iterationCount,
        MaxResponseTokens: 2048,
        Temperature: 0.7
      });
      return response.data;
    } catch (error) {
      console.error('Failed to perform bulk iteration:', error);
      throw error;
    }
  }

  // Utility Methods
  static exportSession(session: VideoPromptSession): void {
    const dataStr = JSON.stringify(session, null, 2);
    const dataUri = 'data:application/json;charset=utf-8,'+ encodeURIComponent(dataStr);
    
    const exportFileDefaultName = `video-prompt-session-${session.id}.json`;
    
    const linkElement = document.createElement('a');
    linkElement.setAttribute('href', dataUri);
    linkElement.setAttribute('download', exportFileDefaultName);
    linkElement.click();
  }

  static importSession(file: File): Promise<VideoPromptSession> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const session = JSON.parse(e.target?.result as string);
          session.id = Date.now().toString(); // Generate new ID
          session.createdAt = new Date(session.createdAt);
          session.lastUpdatedAt = new Date(session.lastUpdatedAt);
          session.iterations = session.iterations.map((iter: any) => ({
            ...iter,
            timestamp: new Date(iter.timestamp)
          }));
          
          const sessions = this.loadSessions();
          sessions.push(session);
          this.saveSessions(sessions);
          
          resolve(session);
        } catch (error) {
          reject(error);
        }
      };
      reader.readAsText(file);
    });
  }
}